/**
 * The numbered save migration registry (task 13.4).
 *
 * A stored document carries the `saveVersion` it was written at. On load the
 * registry is walked from that version up to `SAVE_VERSION`, applying each
 * numbered step in order. A save from a *newer* build is refused outright
 * rather than partially interpreted, and — critically — is left on disk
 * untouched, so opening an old build on a device does not destroy the save a
 * new one wrote (`save-system` spec, "Newer save refused").
 *
 * Two rules for anyone adding a migration:
 *
 *   1. **Key the step by the version it migrates *from*.** `MIGRATIONS[1]`
 *      turns a v1 document into a v2 one. Adding a step means adding one entry
 *      and bumping `SAVE_VERSION`; nothing else in the application changes.
 *   2. **Never lose a completion.** A migration may drop something it cannot
 *      honestly carry forward — an in-progress run written before runs were
 *      resumable is genuinely unresumable — but mission records, banked
 *      Corporate Experience and owned perks are the player's history and are
 *      only ever backfilled, never discarded.
 *
 * Migrations take and return loosely typed records rather than `SaveDocument`,
 * because the shape they operate on is by definition *not* the current one. The
 * final cast happens once, at the end of `migrate`, after every step has run.
 */
import { DEFAULT_SETTINGS, normaliseSettings } from '../settings.js';
import { EMPTY_CAREER, SAVE_VERSION, type SaveDocument } from './types.js';

/** A document mid-migration: the right keys, none of the guarantees. */
type Loose = Record<string, unknown>;

const asRecord = (value: unknown): Loose =>
  typeof value === 'object' && value !== null ? (value as Loose) : {};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Thrown when a save was written by a build newer than this one. */
export class SaveTooNewError extends Error {
  readonly found: number;
  readonly supported: number;

  constructor(found: number, supported: number) {
    super(
      `A newer version of the game wrote this save, in save format ${String(found)}. This version of the game reads save format ${String(supported)}. Update the game to open this save.`,
    );
    this.name = 'SaveTooNewError';
    this.found = found;
    this.supported = supported;
  }
}

/** Thrown when stored data is not a save document at all. */
export class SaveUnreadableError extends Error {}

/**
 * 1 → 2.
 *
 * Three changes landed together with the feedback and career surfaces: mission
 * results started recording whether they were a completion and what they were
 * worth, profiles started remembering which onboarding prompts had been
 * dismissed, and the in-progress run became a resumable record rather than a
 * line of text for the title screen.
 *
 * The first two are backfilled. A v1 result predates the distinction between a
 * scored attempt and a clearance, and every v1 result was written by a *win* —
 * losses were not recorded at all before this version — so `completed` is true.
 * `cxpAwarded` is left at zero rather than reconstructed from the score: the
 * banked total is already correct, and inventing a per-mission figure would let
 * a replay bank the difference against a number nobody earned.
 *
 * The run is dropped. A v1 run carried no `GameState`, so there is nothing to
 * resume; keeping the summary would leave Continue on the title screen offering
 * a run that could only restart.
 */
function toVersion2(doc: Loose): Loose {
  const profiles = asArray(doc['profiles']).map((entry) => {
    const profile = asRecord(entry);
    const progress = asArray(profile['progress']).map((record) => {
      const mission = asRecord(record);
      const best = asRecord(mission['best']);
      return {
        ...mission,
        best: Object.fromEntries(
          Object.entries(best).map(([difficultyId, value]) => {
            const result = asRecord(value);
            return [
              difficultyId,
              {
                grade: typeof result['grade'] === 'string' ? result['grade'] : '',
                score: typeof result['score'] === 'number' ? result['score'] : 0,
                assisted: result['assisted'] === true,
                completed: true,
                cxpAwarded: 0,
                days: typeof result['days'] === 'number' ? result['days'] : 0,
                sends: typeof result['sends'] === 'number' ? result['sends'] : 0,
              },
            ];
          }),
        ),
      };
    });

    return {
      ...profile,
      progress,
      career: { ...EMPTY_CAREER, ...asRecord(profile['career']) },
      onboarding: asArray(profile['onboarding']).filter((id) => typeof id === 'string'),
      run: null,
    };
  });

  return { ...doc, saveVersion: 2, profiles };
}

/**
 * 2 → 3.
 *
 * Settings gained `messageSpace`, the split between the message feed and the
 * composer dock. Every existing save is a save written before the player could
 * move that split, so each profile takes the default — `standard`, which is the
 * layout those saves were played at.
 *
 * `normaliseSettings` would fill the same value in on load, and deliberately
 * still does: a save that skipped this step, hand-edited or written by a build
 * mid-flight, has to render too. The numbered step is what `save-system`
 * requires for a shape change, and it is what makes the default a decision
 * recorded once rather than a fallback applied every load.
 */
function toVersion3(doc: Loose): Loose {
  const profiles = asArray(doc['profiles']).map((entry) => {
    const profile = asRecord(entry);
    return {
      ...profile,
      settings: normaliseSettings(profile['settings']),
    };
  });

  return { ...doc, saveVersion: 3, profiles };
}

/**
 * 3 → 4.
 *
 * Profiles gained `tutorialsCompleted`. Every existing save was written before
 * a guided walkthrough existed to complete, so each profile takes the empty
 * list — which is the truthful answer, not merely the convenient one: a player
 * who has never seen a walkthrough has not been taught by one, and the
 * contextual onboarding cards should still meet them.
 */
function toVersion4(doc: Loose): Loose {
  const profiles = asArray(doc['profiles']).map((entry) => ({
    ...asRecord(entry),
    tutorialsCompleted: [],
  }));

  return { ...doc, saveVersion: 4, profiles };
}

/**
 * 4 → 5.
 *
 * Settings gained `colour` — whether the expressive palette is applied — and
 * `motion` gained a fourth value, `calm`.
 *
 * Only the first is a shape change, and it is supplied at its default, `vivid`.
 * The second deliberately rewrites nothing: `system`, `full` and `reduced` all
 * remain valid and all still mean what they meant, so a profile that chose
 * `reduced` keeps `reduced`. Widening the permitted values of a setting is not
 * a reason to touch a stored one (`save-system` spec, "Widening motion does not
 * rewrite stored values").
 *
 * `normaliseSettings` would fill `colour` in on load and deliberately still
 * does, for a save that skipped this step. The numbered step is what the spec
 * requires for a shape change, and it is what makes the default a decision
 * recorded once rather than a fallback applied every load.
 */
function toVersion5(doc: Loose): Loose {
  const profiles = asArray(doc['profiles']).map((entry) => {
    const profile = asRecord(entry);
    return {
      ...profile,
      settings: normaliseSettings(profile['settings']),
    };
  });

  return { ...doc, saveVersion: 5, profiles };
}

/** Keyed by the version each step migrates *from*. */
export const MIGRATIONS: Readonly<Record<number, (doc: Loose) => Loose>> = {
  1: toVersion2,
  2: toVersion3,
  3: toVersion4,
  4: toVersion5,
};

/** What `migrate` did, for the caller to report or record. */
export interface MigrationResult {
  readonly doc: SaveDocument;
  /** The version the stored document was written at. */
  readonly from: number;
  /** The version numbers whose steps ran, in order. */
  readonly applied: readonly number[];
}

/**
 * Brings a stored document up to `SAVE_VERSION`.
 *
 * Throws `SaveTooNewError` for a save from a later build and
 * `SaveUnreadableError` for anything that is not a save document — both of
 * which the store turns into "start fresh and say so", never into a silent
 * overwrite of what is on disk.
 */
export function migrate(raw: unknown): MigrationResult {
  const doc = asRecord(raw);
  const version = doc['saveVersion'];
  const profiles = doc['profiles'];

  if (typeof version !== 'number' || !Array.isArray(profiles)) {
    throw new SaveUnreadableError('That is not a Managing Up save.');
  }
  // Distinguished from the above deliberately: a save document with an empty
  // profile array is recognisably ours and recognisably broken, and telling the
  // player which of the two they have is the difference between "wrong file"
  // and "this file is damaged".
  if (profiles.length === 0) {
    throw new SaveUnreadableError('That Managing Up save is empty.');
  }
  if (version > SAVE_VERSION) throw new SaveTooNewError(version, SAVE_VERSION);

  const applied: number[] = [];
  let current: Loose = doc;
  for (let step = version; step < SAVE_VERSION; step += 1) {
    const migration = MIGRATIONS[step];
    if (migration === undefined) {
      // A gap in the registry is a build error, not a player's problem, and it
      // is better to say so than to hand the application a half-migrated shape.
      throw new SaveUnreadableError(
        `This version of the game cannot read save format ${String(step)}. Update the game to open this save.`,
      );
    }
    current = migration(current);
    applied.push(step);
  }

  return { doc: normalise(current), from: version, applied };
}

/**
 * The last pass every loaded document gets, migrated or not.
 *
 * Fills in what a hand-edited or partially-written file may be missing, so the
 * rest of the application can rely on its invariants — every profile has
 * settings, a career and an onboarding list, and the active pointer names a
 * profile that exists.
 */
export function normalise(raw: Loose): SaveDocument {
  const profiles = asArray(raw['profiles']).map((entry, index) => {
    const profile = asRecord(entry);
    return {
      id:
        typeof profile['id'] === 'string' && profile['id']
          ? profile['id']
          : `profile-${String(index + 1)}`,
      name: typeof profile['name'] === 'string' && profile['name'] ? profile['name'] : 'Player',
      createdAt: typeof profile['createdAt'] === 'string' ? profile['createdAt'] : '',
      settings:
        profile['settings'] === undefined
          ? DEFAULT_SETTINGS
          : normaliseSettings(profile['settings']),
      progress: asArray(profile['progress']),
      career: { ...EMPTY_CAREER, ...asRecord(profile['career']) },
      onboarding: asArray(profile['onboarding']).filter((id) => typeof id === 'string'),
      tutorialsCompleted: asArray(profile['tutorialsCompleted']).filter(
        (id) => typeof id === 'string',
      ),
      run: profile['run'] ?? null,
    };
  }) as SaveDocument['profiles'];

  const active = raw['activeProfileId'];
  return {
    saveVersion: SAVE_VERSION,
    activeProfileId:
      typeof active === 'string' && profiles.some((profile) => profile.id === active)
        ? active
        : (profiles[0]?.id ?? ''),
    profiles,
  };
}
